import CheckIcon from '@mui/icons-material/Check'
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  Chip as MuiChip,
  TablePagination,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { ListFilterGroup } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import moment from 'moment'
import { VehicleToDoListParameters } from 'parameters'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'

export default function NeedsCheckInToDoListDetails() {
  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('md'))

  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicles: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'sentToBrokerDate,exporter',
    order: 'asc'
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const getNeedsCheckInToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getNeedsCheckInToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs Check In To Do List')
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getNeedsCheckInToDoList(parameters)
  }, [getNeedsCheckInToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  const isDateExceedingLimit = (date: string | Date, limit: number) => {
    if (!date || isNaN(new Date(date).getTime())) return false
    return moment().diff(moment(date), 'days') >= limit
  }

  const formatDateToMMDDYYYY = (date: string | Date | undefined): string => {
    if (!date || isNaN(new Date(date).getTime())) return ''
    return formatDate(date)
  }
  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto' alignContent='center'>
              <Grid item container xs={12} justifyContent='space-between'>
                <ListFilterGroup>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    margin='dense'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    size='small'
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='sentToBrokerDate'>Sent to Broker Date</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>
                  </TextField>

                  {['', 'exporter', 'vin', 'year', 'make', 'model', 'importerStockNumber'].includes(filterField) && (
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        disabled={!filterField}
                        label='Filter Value'
                        margin='dense'
                        onChange={e => {
                          setFilterValue(e.target.value)
                        }}
                        size='small'
                        value={filterValue || ''}
                      />
                    </Grid>
                  )}

                  {filterField === 'sentToBrokerDate' && (
                    <>
                      <Grid container>
                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                            label='Filter Date From'
                            onChange={e => {
                              setFilterValueFrom(e.target.value)
                            }}
                            type='date'
                            value={filterValueFrom}
                          />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                            label='Filter Date To'
                            onChange={e => {
                              setFilterValueTo(e.target.value)
                            }}
                            type='date'
                            value={filterValueTo}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='inherit'
                      disabled={
                        !filterField || (filterField === 'sentToBrokerDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                      }
                      variant='contained'
                      onClick={() => {
                        console.log('filterField', filterField)
                        console.log('filterValue', filterValue)
                        if (filterField === 'sentToBrokerDate') {
                          setParameters({
                            ...parameters,
                            sentToBrokerDateFrom: filterValueFrom,
                            sentToBrokerDateTo: filterValueTo
                          })
                        } else {
                          setParameters({
                            ...parameters,
                            [filterField]: filterValue
                          })
                        }

                        setFilterField('')
                        setFilterValue('')
                        setFilterValueFrom('')
                        setFilterValueTo('')
                      }}
                    >
                      APPLY FILTER
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='secondary'
                      disabled={
                        !Boolean(
                          parameters.sentToBrokerDateFrom ||
                            parameters.sentToBrokerDateTo ||
                            parameters.exporter ||
                            parameters.vin ||
                            parameters.year ||
                            parameters.make ||
                            parameters.model ||
                            parameters.importerStockNumber
                        )
                      }
                      variant='text'
                      onClick={() => {
                        setParameters({
                          ...parameters,
                          sentToBrokerDateFrom: 'undefined',
                          sentToBrokerDateTo: 'undefined',
                          exporter: 'undefined',
                          vin: 'undefined',
                          year: undefined,
                          make: 'undefined',
                          model: 'undefined',
                          importerStockNumber: undefined
                        })
                      }}
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>
                </ListFilterGroup>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.sentToBrokerDateFrom && parameters.sentToBrokerDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Sent To Broker Date from "${moment(parameters.sentToBrokerDateFrom).format(
                      'M/D/yyyy'
                    )}" to "${moment(parameters.sentToBrokerDateTo).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        sentToBrokerDateFrom: undefined,
                        sentToBrokerDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${parameters.vin}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is "${parameters.year}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${parameters.make}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${parameters.model}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock# contains "${parameters.importerStockNumber}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        importerStockNumber: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {vehicles?.map(vehicle => (
        <Card sx={{ mb: 2, mt: 2 }}>
          <CardContent>
            <Grid container spacing={1} alignItems='flex-start'>
              <Grid item xs={12}>
                <LabelField
                  label='Sent to Broker Date'
                  value={formatDateToMMDDYYYY(vehicle.sentToBrokerDateTime)}
                  error={!!vehicle.sentToBrokerDateTime && isDateExceedingLimit(vehicle.sentToBrokerDateTime, 4)}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Exporter' value={vehicle?.exporterCompany?.name} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='VIN' value={vehicle.vin} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Year' value={vehicle.year} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Make' value={vehicle.make} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Model' value={vehicle.model} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Importer Stock #' value={vehicle.importerStockNumber} />
              </Grid>

              <Grid item xs={12} container alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    endIcon={<CheckIcon />}
                    fullWidth={fullWidth}
                    onClick={() => {
                      navigate('/vehicles/' + vehicle.vehicleGuid + '/check-in')
                    }}
                    variant='text'
                  >
                    CHECK IN
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Grid item xs={12}>
        <ExplanationAccordion>
          Vehicles appear in this to do list if the Inbound Trip the vehicle is associated with has been locked, but the vehicle
          check in process has not been completed. || The Needs Check-In “To Do List” cards “Sent to Broker Date” value will be
          in bold and red if current date is ≥ the day the “To Broker PDF” email was sent + 4 days. || The Needs Check-In “To Do
          List” cards are sorted by “Sent to Broker Date” oldest to newest and then by Exporter alphabetically. || The Needs
          Check-In “To Do List” is a card screen intended for users on a phone. || If an Inbound Trip arrives but an expected
          vehicle is missing, users will contact the exporter/transporter and then cancel the vehicle if the vehicle is never
          coming or if it is going to be coming on a different PAPS/Inbound Trip. || If an unexpected vehicle arrives on an
          Inbound Trip, this vehicle will not be in VTS2 and will be resolved outside of the software. || If an expected vehicle
          arrives but its VIN plate does not belong to that vehicle, the user will navigate to that vehicles Vehicle Details and
          press “Cancel Import” for that vehicle. This involves unlocking the trip, unlocking the PAPS, removing the vehicle
          from the PAPS, and then pressing “CANCEL IMPORT” for that vehicle. || If an expected vehicle arrives but is missing
          its VIN plate, if the user can tell which expected vehicle it is that has arrived based on the vehicles description,
          the user will press “Cancel Import” for that vehicle. This involves unlocking the trip, unlocking the PAPS, removing
          the vehicle from the PAPS, and then pressing “CANCEL IMPORT” for that vehicle. ||
        </ExplanationAccordion>
      </Grid>

      <Grid item xs={12}>
        <TablePagination
          component='div'
          count={totalCount}
          onPageChange={(_, page: number) => {
            setParameters({
              ...parameters,
              page
            })
          }}
          onRowsPerPageChange={e => {
            setParameters({
              ...parameters,
              page: 0,
              pageSize: e.target.value as unknown as number
            })
          }}
          page={parameters.page}
          rowsPerPage={parameters.pageSize}
          labelRowsPerPage={
            <Grid container spacing={1}>
              <Grid item>Order By:</Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, orderBy: e.target.value })
                  }}
                  select
                  size='small'
                  value={parameters.orderBy}
                  variant='standard'
                >
                  <MenuItem value='sentToBrokerDate'>Sent To Broker Date</MenuItem>

                  <MenuItem value='exporter'>Exporter</MenuItem>

                  <MenuItem value='vin'>VIN</MenuItem>

                  <MenuItem value='year'>Year</MenuItem>

                  <MenuItem value='make'>Make</MenuItem>

                  <MenuItem value='model'>Model</MenuItem>

                  <MenuItem value='importerStockNumber'>Importer Stock#</MenuItem>
                </TextField>
              </Grid>

              <Grid item>
                <TextField
                  InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                  onChange={e => {
                    setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                  }}
                  select
                  size='small'
                  value={parameters.order}
                  variant='standard'
                >
                  <MenuItem value='asc'>Ascending</MenuItem>

                  <MenuItem value='desc'>Descending</MenuItem>
                </TextField>
              </Grid>

              <Grid item sx={{ alignSelf: 'stretch' }}>
                <Divider orientation='vertical' />
              </Grid>

              <Grid item>Cards Per Page:</Grid>
            </Grid>
          }
          rowsPerPageOptions={[
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 }
          ]}
          SelectProps={{ fullWidth: false }}
        />
      </Grid>
    </>
  )
}
